import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createCategory, updateCategory, getCategories } from '../../services/firestoreService';
import { auth } from '../../firebase';
import { useNotifications } from '../../contexts/NotificationContext';

// Default category images
const DEFAULT_IMAGES = [
  'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=500',
  'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=500',
  'https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=500',
  'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=500',
  'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=500',
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=500',
  'https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?w=500',
  'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500',
];

// Get random default image
const getRandomDefaultImage = () => {
  return DEFAULT_IMAGES[Math.floor(Math.random() * DEFAULT_IMAGES.length)];
};

const CreateCategoryDrawer = ({ isOpen, onClose, onSuccess, editMode = false, categoryData = null }) => {
  const { notify } = useNotifications();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [imageUploadError, setImageUploadError] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (editMode && categoryData) {
      setFormData({
        name: categoryData.name || '',
        description: categoryData.description || '',
        imageUrl: categoryData.image || '',
      });
      setUploadedFileName('');
      setImageUploadError('');
    } else {
      setFormData({
        name: '',
        description: '',
        imageUrl: '',
      });
      setUploadedFileName('');
      setImageUploadError('');
    }
  }, [editMode, categoryData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length > 120) {
      newErrors.description = 'Description must be 120 characters or less';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Check for duplicate category name
      const userId = auth.currentUser?.uid;
      const categoriesResult = await getCategories(userId);

      if (categoriesResult.success) {
        const existingCategory = categoriesResult.data.find(cat =>
          cat.name.toLowerCase() === formData.name.trim().toLowerCase() &&
          (!editMode || cat.id !== categoryData?.id) // Exclude current category in edit mode
        );

        if (existingCategory) {
          setErrors({
            name: `A category named "${existingCategory.name}" already exists. Please choose a different name.`
          });
          setLoading(false);
          return;
        }
      }

      if (editMode && categoryData) {
        // Update existing category
        const updateData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          // Only update image if user changed it, otherwise keep existing
          image: formData.imageUrl.trim() || categoryData.image,
          slug: formData.name
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-'),
        };

        const result = await updateCategory(categoryData.id, updateData);

        if (result.success) {
          notify({
            title: 'Category Updated',
            message: `"${formData.name}" has been updated successfully`,
            type: 'success',
          });

          onSuccess();
          onClose();
        } else {
          notify({
            title: 'Error',
            message: result.error || 'Failed to update category',
            type: 'error',
          });
        }
      } else {
        // Create new category
        const newCategoryData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          image: formData.imageUrl.trim() || getRandomDefaultImage(),
          slug: formData.name
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-'),
          createdBy: auth.currentUser.uid,
          isSystemCategory: false,
        };

        const result = await createCategory(newCategoryData);

        if (result.success) {
          notify({
            title: 'Category Created',
            message: `"${formData.name}" has been created successfully`,
            type: 'success',
          });

          // Reset form
          setFormData({
            name: '',
            description: '',
            imageUrl: '',
          });

          onSuccess();
          onClose();
        } else {
          notify({
            title: 'Error',
            message: result.error || 'Failed to create category',
            type: 'error',
          });
        }
      }
    } catch (err) {
      notify({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        imageUrl: '',
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        .category-drawer-form::-webkit-scrollbar {
          width: 6px;
        }
        .category-drawer-form::-webkit-scrollbar-track {
          background: #F1F5F9;
          border-radius: 10px;
          margin: 4px 0;
        }
        .category-drawer-form::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 10px;
          border: 1px solid #E2E8F0;
        }
        .category-drawer-form::-webkit-scrollbar-thumb:hover {
          background: #94A3B8;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[500px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-800">
              {editMode ? 'Edit Category' : 'Create Category'}
            </h2>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-6 category-drawer-form"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#CBD5E1 #F1F5F9'
            }}
          >
            <div className="space-y-5">
              {/* Category Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Technology, Medicine, History"
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                    errors.name
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-[#6B7A8F]'
                  }`}
                  disabled={loading}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of this category"
                  rows={4}
                  maxLength={120}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                    errors.description
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-[#6B7A8F]'
                  }`}
                  disabled={loading}
                />
                <div className="flex justify-between items-center mt-1">
                  <div>
                    {errors.description && (
                      <p className="text-red-500 text-xs">{errors.description}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.description.length}/120
                  </p>
                </div>
              </div>

              {/* Image URL */}
              <div>
                <div className="flex flex-col gap-1 mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    Cover Image <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-600">
                    Add a cover image to make your category more attractive. Upload a file or paste an image URL.
                  </p>
                </div>

                {/* Single Row: Input + Upload Button */}
                <div className="flex items-stretch gap-3">
                  <input
                    type="text"
                    value={uploadedFileName || (formData.imageUrl && !formData.imageUrl.startsWith('data:') ? formData.imageUrl : '')}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      setUploadedFileName('');

                      if (value) {
                        try {
                          const url = new URL(value);
                          if (url.protocol === 'http:' || url.protocol === 'https:') {
                            setFormData(prev => ({ ...prev, imageUrl: value }));
                            setImageUploadError('');
                          } else {
                            setImageUploadError('Only HTTP/HTTPS URLs are allowed');
                          }
                        } catch (err) {
                          setFormData(prev => ({ ...prev, imageUrl: value }));
                          setImageUploadError('');
                        }
                      } else {
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                        setImageUploadError('');
                      }
                    }}
                    placeholder={uploadedFileName ? "File uploaded" : "Paste image address (optional)"}
                    disabled={!!uploadedFileName || loading}
                    style={{ height: '44px' }}
                    className={`flex-1 px-4 border border-gray-300 rounded-lg focus:outline-none text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-[#6B7A8F] focus:ring-2 focus:ring-[#6B7A8F]/20 ${uploadedFileName ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                  <label className="cursor-pointer flex-shrink-0">
                    <div
                      style={{ height: '44px' }}
                      className="px-4 border border-gray-300 bg-white rounded-lg hover:border-[#6B7A8F] hover:bg-gray-50 transition-all flex items-center gap-2"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-medium text-gray-900">Upload</span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      className="hidden"
                      disabled={loading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                          if (!validTypes.includes(file.type)) {
                            setImageUploadError('Only JPG, PNG, GIF, and WebP images are allowed');
                            return;
                          }

                          if (file.size > 512000) {
                            setImageUploadError('Image size must be less than 500KB');
                            return;
                          }

                          const fileName = file.name;
                          if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
                            setImageUploadError('Invalid file name');
                            return;
                          }

                          setImageUploadError('');

                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const result = reader.result;
                            if (typeof result === 'string' && result.startsWith('data:image/')) {
                              setFormData(prev => ({ ...prev, imageUrl: result }));
                              setUploadedFileName(fileName);
                            } else {
                              setImageUploadError('Invalid image file');
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>

                {/* Error Message */}
                {imageUploadError && (
                  <p className="text-red-500 text-xs mt-1">{imageUploadError}</p>
                )}

                {/* Helper Text */}
                {!imageUploadError && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {formData.imageUrl ? (formData.imageUrl.startsWith('data:') ? 'File uploaded' : 'Custom URL') : 'Will use random default image'}
                    </p>
                    {!formData.imageUrl && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        Auto
                      </span>
                    )}
                  </div>
                )}

                {/* Image Preview */}
                {formData.imageUrl && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Preview:</p>
                    <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = getRandomDefaultImage();
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t p-6 bg-gray-50">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-[#6B7A8F] hover:bg-[#5a6675] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {loading
                  ? (editMode ? 'Updating...' : 'Creating...')
                  : (editMode ? 'Update Category' : 'Create Category')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCategoryDrawer;
